<script setup lang="ts">
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

const {
    navigationItems,
    openedCategory,
    activeItem,
    closePopover,
    clearPopoverTimeout
} = useMegaMenu()

const selectedManufacturers = useSelectedManufacturers(activeItem)

const megaMenuPopover = shallowRef()

const { activate, deactivate } = useFocusTrap(megaMenuPopover, { allowOutsideClick: true })

watch(openedCategory, async (newValue, oldValue) => {
    if (newValue && !oldValue) {
        await nextTick()
        activate()
    } else if (!newValue && oldValue) {
        deactivate()
    }
})
</script>

<template>
    <!-- Backdrop -->
    <Transition
        enter-active-class="transition-opacity duration-150"
        leave-active-class="transition-opacity duration-100"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
    >
        <div
            v-if="openedCategory"
            class="fixed inset-0 bg-black/50 z-40"
            @click="closePopover()"
        />
    </Transition>

    <!-- Flyout -->
    <Transition
        enter-active-class="transition-all duration-150 ease-out"
        leave-active-class="transition-all duration-100 ease-in"
        enter-from-class="opacity-0 -translate-y-full"
        enter-to-class="opacity-100 translate-y-0"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-full"
    >
        <div
            v-show="openedCategory"
            ref="megaMenuPopover"
            class="absolute top-full left-0 right-0 z-50 w-full bg-base-100 shadow-lg p-10"
            @mouseenter="clearPopoverTimeout()"
            @mouseleave="closePopover({ delay: 100 })"
        >
            <div class="mx-auto w-full max-w-8xl px-2 relative">
                <FoundationButton
                    size="small"
                    square
                    class="absolute top-0 right-0"
                    @click="closePopover()"
                >
                    <span class="sr-only">{{ $t('megaMenu.popOver.close') }}</span>
                    <FoundationIcon name="x" />
                </FoundationButton>
                <template v-for="item in navigationItems ?? []" :key="item.id">
                    <div
                        v-show="openedCategory === item.id && activeItem?.children?.length"
                        :id="`menu-${item.id}`"
                        class="flex flex-wrap flex-col items-start gap-4 max-h-[550px]"
                    >
                        <MegaMenuItem
                            v-for="subItem in item.children"
                            v-show="activeItem"
                            :key="subItem.id"
                            class="text-neutral max-w-[20%]"
                            :item="subItem"
                            @navigate="closePopover()"
                        />

                        <div
                            v-if="openedCategory === item.id && selectedManufacturers.length"
                            class="flex flex-col max-w-[20%]"
                        >
                            <span class="text-neutral font-bold uppercase">
                                {{ $t('megaMenu.topBrands.title') }}
                            </span>
                            <div class="flex flex-col">
                                <FoundationLink
                                    v-for="manufacturer in selectedManufacturers"
                                    :key="manufacturer.id"
                                    :href="`${manufacturer.url}/${item?.seoUrls?.[0]?.seoPathInfo}`"
                                    class="font-normal text-neutral min-h-6"
                                    @click="closePopover()"
                                >
                                    {{ manufacturer.name }}
                                </FoundationLink>
                            </div>
                            <FoundationLink
                                :href="`/marken/${item.name.toLowerCase()}`"
                                class="text-primary font-bold mt-2"
                                @click="closePopover()"
                            >
                                {{ $t('megaMenu.topBrands.viewAll', { category: item.name }) }}
                            </FoundationLink>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </Transition>
</template>
